/* eslint-disable react/no-array-index-key */
import { faSparkles } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Person } from '@mui/icons-material';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { isEmpty } from 'lodash-es';
import get from 'lodash-es/get';
import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';

import { CUSTOM_SEGMENTS_LIST_URL } from '../../../constants/routeUrls';
import { useConstants } from '../../../hooks/useConstants';
import useModal from '../../../hooks/useModal';
import useResolveExternalSegment from '../../../hooks/useResolveExternalSegment';
import { Link } from '../../../inmarkit';
import posthog from '../../../util/posthog';
import { headerStyles } from '../../Contracts/ContractsTable';
import AiAudienceSearch, { SelectedSegment } from '../../CustomSegments/AiAudienceSearch';
import CustomSegmentsForm from '../../CustomSegments/CustomSegmentsForm';
import Select from '../../forms/Select';
import SyndicatedSegmentsModal from './SyndicatedSegmentsModal';
import TargetingAudiencesField from './TargetingAudiencesField';

interface TargetingAudiencesTableProps {
  name: string;
  exclude?: boolean;
}

function TargetingAudiencesTable({ name, exclude = false }: TargetingAudiencesTableProps) {
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const [selectedAudiences, setSelectedAudiences] = useState([]);
  const { constants } = useConstants();

  const { control, getValues, watch } = useFormContext();

  const TARGETING_DAYS = get(constants, 'SEGMENT.TARGETING_DAYS', []).map((item) => ({
    value: item[0],
    label: item[1],
  }));

  const { fields, append, remove } = useFieldArray({
    name,
    control,
  });

  const targetType = watch('targetType');
  const audienceSegmentsCount = useMemo(
    () => fields.filter((unused, index) => getValues(`${name}.${index}.type`) === 'custom').length,
    [name, fields, getValues]
  );

  const removeSelectedAudiences = () => {
    remove(selectedAudiences);
    setSelectedAudiences([]);
    setSelectAllChecked(false);
  };

  const selectAll = (event) => {
    const { checked } = event.target;

    setSelectAllChecked(checked);

    if (checked) {
      const indexArray = [];
      for (let i = 0; i < fields.length; i += 1) {
        indexArray.push(i);
      }
      setSelectedAudiences(indexArray);
    } else {
      setSelectedAudiences([]);
    }
  };

  useEffect(() => {
    if (selectedAudiences.length !== 0) {
      if (selectAllChecked && selectedAudiences.length !== audienceSegmentsCount) {
        setSelectAllChecked(false);
      } else if (!selectAllChecked && selectedAudiences.length === audienceSegmentsCount) {
        setSelectAllChecked(true);
      }
    } else {
      setSelectAllChecked(false);
    }
  }, [selectAllChecked, selectedAudiences, setSelectAllChecked, audienceSegmentsCount]);

  const headerStylesCss = headerStyles();

  const resolveExternalSegment = useResolveExternalSegment();

  const [showAiSearchModal, hideAiSearchModal] = useModal(
    () => (
      <Box p={3} sx={{ minWidth: '80vw', minHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Search by AI Description
        </Typography>
        <AiAudienceSearch
          onUseSegment={async (segment: SelectedSegment) => {
            // Resolve the AI result to the backfilled ventura segment before
            // attaching. Without a resolved segment_id the row would target
            // nothing, so on a miss/error we surface a message and keep the modal
            // open so the user can pick another result.
            const resolved = await resolveExternalSegment(segment.externalSource, segment.externalId);
            if (resolved.status === 'not_found') {
              return {
                error: `"${segment.name}" isn't available as a segment yet. Try another result.`,
              };
            }
            if (resolved.status === 'error') {
              return {
                error: `Couldn't look up "${segment.name}" right now. Please try again.`,
              };
            }
            const segmentIdRaw = resolved.segment.segment_id ?? resolved.segment.id;
            const segmentId = typeof segmentIdRaw === 'string' ? Number(segmentIdRaw) : segmentIdRaw;
            if (segmentId == null || Number.isNaN(segmentId)) {
              return {
                error: `Couldn't use "${segment.name}" because it didn't resolve to a valid segment id.`,
              };
            }
            append({
              segmentId,
              name: segment.name,
              type: 'custom',
              targetingDay: -1,
            });
            hideAiSearchModal();
            return { error: null };
          }}
        />
      </Box>
    ),
    { clickOutsideCloses: true },
    [append, resolveExternalSegment]
  );

  const [showSyndicatedSegmentsModal, hideSyndicatedSegmentsModal] = useModal(
    () => (
      <SyndicatedSegmentsModal
        onClose={hideSyndicatedSegmentsModal}
        onSelect={(segmentId, segmentName) => {
          hideSyndicatedSegmentsModal();
          append({
            segmentId,
            name: segmentName,
            type: 'custom',
            targetingDay: -1,
          });
        }}
      />
    ),
    { clickOutsideCloses: true },
    [append]
  );

  const [showNewCustomSegmentModal, hideCustomSegmentModal] = useModal(
    () => (
      <CustomSegmentsForm
        onCreateSuccess={({ id, name: customSegmentName, saveAndAdd }) => {
          hideCustomSegmentModal();
          if (saveAndAdd) {
            append({
              segmentId: id,
              name: customSegmentName,
              type: 'custom',
              targetingDay: -1,
            });
          }
        }}
        renderFooter={(onSave, isDirty, form) => (
          <>
            <Button variant="outlined" onClick={hideCustomSegmentModal}>
              Close
            </Button>
            <Button
              onClick={() => {
                form.setValue('saveAndAdd', false);
                onSave();
              }}
              disabled={!isDirty}
            >
              Save
            </Button>
            <Button
              onClick={() => {
                form.setValue('saveAndAdd', true);
                onSave();
              }}
              disabled={!isDirty}
            >
              Save and Add to Ad Group
            </Button>
          </>
        )}
      />
    ),
    null,
    [append]
  );

  return (
    <TableContainer className={headerStylesCss.root}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" px={2} pt={2}>
        <Link href={CUSTOM_SEGMENTS_LIST_URL}>View All Segments</Link>
        <Stack spacing={1} direction="row" justifyContent="end">
          {!isEmpty(fields) && (
            <Button onClick={removeSelectedAudiences} variant="outlined" color="error">
              Delete
            </Button>
          )}
          <Button
            variant="outlined"
            onClick={() =>
              append({
                segmentId: null,
                name: '',
                type: 'custom',
                targetingDay: -1,
              })
            }
          >
            Add Audience
          </Button>
          <Button
            variant="outlined"
            startIcon={<FontAwesomeIcon icon={faSparkles} />}
            onClick={() => {
              posthog.capture('ai_audience_search_opened', { context: 'ad_group_targeting' });
              showAiSearchModal();
            }}
          >
            Search by AI Description
          </Button>
          <Button variant="outlined" onClick={showSyndicatedSegmentsModal}>
            Add Syndicated Segment
          </Button>
          <Button onClick={showNewCustomSegmentModal}>Create New Audience</Button>
        </Stack>
      </Stack>
      {!exclude && audienceSegmentsCount === 0 && targetType.includes('Preceptivity') && (
        <Box component="span" sx={{ p: 2 }}>
          <Alert icon={<Person fontSize="inherit" />} aria-label="Preceptivity Automation">
            <React.Fragment key=".0">
              <AlertTitle>Preceptivity Automation</AlertTitle>
              Audiences for your attached locations will be auto-created and assigned 24 hours before this Ad
              Group&apos;s start date. Manually adding segments will override this process
            </React.Fragment>
          </Alert>
        </Box>
      )}
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead sx={{}}>
          <TableRow>
            <TableCell width={40}>
              <Checkbox onChange={selectAll} checked={selectAllChecked} inputProps={{ alt: 'Select All' }} />
            </TableCell>
            <TableCell>Audience Segment</TableCell>
            {!exclude && <TableCell>Day</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {fields.map((item, index) => (
            <Controller
              key={item.id}
              name={`${name}.${index}`}
              control={control}
              render={({ field }) =>
                field.value.type === 'custom' && (
                  <TableRow key={item.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                    <TableCell scope="row">
                      <Checkbox
                        inputProps={{ alt: 'Select' }}
                        onChange={(event) => {
                          if (event.target.checked) {
                            setSelectedAudiences([...selectedAudiences, index]);
                          } else {
                            setSelectedAudiences(selectedAudiences.filter((i) => i !== index));
                          }
                        }}
                        checked={Boolean(selectedAudiences.includes(index))}
                      />
                    </TableCell>
                    <TableCell scope="row">
                      <TargetingAudiencesField name={`${name}.${index}.segmentId`} valueLabel={field.value.name} />
                    </TableCell>
                    {!exclude && (
                      <TableCell scope="row">
                        <Select
                          name={`${name}.${index}.targetingDay`}
                          label="Day"
                          placeholder="Day"
                          options={TARGETING_DAYS}
                        />
                      </TableCell>
                    )}
                  </TableRow>
                )
              }
            />
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default TargetingAudiencesTable;
